import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface SectionData {
  id: string;
  section_name: string;
  section_type: string;
  target_date: string | null;
  countdown_enabled: boolean;
}

interface PhaseData {
  id: string;
  phase_title: string;
  display_order: number;
}

interface ItemData {
  id: string;
  item_text: string;
  phase_id: string | null;
  display_order: number;
  is_ready: boolean;
  is_bundled: boolean;
  is_secured: boolean;
  notes: string | null;
}

export const useSharing = () => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const getSectionData = async (sectionId: string): Promise<{
    section: SectionData;
    phases: PhaseData[];
    items: ItemData[];
  } | null> => {
    if (!user) return null;

    try {
      // Get section info
      const { data: sectionData, error: sectionError } = await supabase
        .from('checklist_sections')
        .select('id, section_name, section_type, target_date, countdown_enabled')
        .eq('id', sectionId)
        .eq('user_id', user.id)
        .single();

      if (sectionError || !sectionData) {
        throw new Error('Section not found');
      }

      // Get phases
      const { data: phasesData, error: phasesError } = await supabase
        .from('checklist_phases')
        .select('id, phase_title, display_order')
        .eq('section_id', sectionId)
        .eq('user_id', user.id)
        .order('display_order');

      if (phasesError) {
        throw new Error('Failed to fetch phases');
      }

      // Get items
      const { data: itemsData, error: itemsError } = await supabase
        .from('checklist_items')
        .select('id, item_text, phase_id, display_order, is_ready, is_bundled, is_secured, notes')
        .eq('section_id', sectionId)
        .eq('user_id', user.id)
        .order('display_order');

      if (itemsError) {
        throw new Error('Failed to fetch items');
      }

      return {
        section: sectionData,
        phases: phasesData || [],
        items: itemsData || [],
      };

    } catch (error) {
      // Errors are logged in development only
      return null;
    }
  };

  return {
    getSectionData,
    loading,
  };
};